var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { OnInit, OnDestroy } from '@angular/core';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { Subject, Observable } from 'rxjs';
import { Select, Store, Actions, ofActionCompleted } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { takeUntil, filter } from 'rxjs/operators';
import { SetSelectedChapterBackground, SetAllChaptersBackground, ResetSelectedChapterBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { FormBuilder, Validators } from '@angular/forms';
import { TabComponent } from 'src/app/shared/components/tabs/tab/tab.component';
import { environment } from 'src/environments/environment';
import { plainToClass } from 'class-transformer';
import { MatSnackBar } from '@angular/material/snack-bar';
import { loftSwatch, studioSwatch, transparentBackground } from './background-config';
import { isEqual } from 'lodash';
import { ColorUtils } from 'src/app/shared/libraries/colors/colors';
export class BackgroundColorDialogComponent {
    constructor(store, actions, fb, snackBar) {
        this.store = store;
        this.actions = actions;
        this.fb = fb;
        this.snackBar = snackBar;
        // environment based preset swatch
        this.presetBackgrounds = environment.exportType === 'module'
            ? plainToClass(BackgroundModel, loftSwatch) // loft
            : plainToClass(BackgroundModel, studioSwatch); // studio
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.initPresets();
        this.initForm();
        // messaging
        this.actions.pipe(ofActionCompleted(SetAllChaptersBackground), takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.snackBar.open('Successfully applied color to all chapters.');
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    initPresets() {
        // track selected preset
        this.presetBackground = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
        this.background$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((background) => {
            this.presetBackground = background;
        });
        // track distinct chapter backgrounds
        this.allChapterBackgrounds$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((backgrounds) => {
            this.distinctBackgrounds = backgrounds.filter((background, index) => {
                return backgrounds.findIndex((searchBackgound) => this.backgroundsEqual(background, searchBackgound)) === index;
            });
        });
    }
    initForm() {
        // get current chapter defaults
        const chapterBackground = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
        // setup form
        this.form = this.fb.group({
            type: ['solid', [Validators.required]],
            color1: ['', [Validators.required]],
            stop1: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
            color2: ['', [Validators.required]],
            stop2: [100, [Validators.required, Validators.min(0), Validators.max(100)]]
        });
        // setup events: set custom background
        this.form.valueChanges
            .pipe(filter(() => this.customTab.isActive), takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.applyFromCustom();
        });
        // set initial form values
        if (chapterBackground) {
            this.setCustomColorForm(chapterBackground);
        }
    }
    handleFormColorSelected(fieldName, color) {
        this.form.get(fieldName)
            .setValue(color);
    }
    handlePresetSelected(background) {
        this.presetBackground = background;
        this.store.dispatch(new SetSelectedChapterBackground(background));
    }
    handleResetClick() {
        this.store.dispatch(new ResetSelectedChapterBackground());
    }
    handleTabChange(tab) {
        // apply custom preset
        if (this.customTab.isActive && this.presetBackground) {
            this.setCustomColorForm(this.presetBackground);
        }
    }
    backgroundsEqual(backgroundA, backgroundB) {
        return isEqual(backgroundA, backgroundB);
    }
    getBackgroundHex(background) {
        const colors = background.colors;
        return ColorUtils.rgbToHex([colors[0], colors[1], colors[2]]);
    }
    getFormBackgroundHex(fieldName) {
        return this.form.get(fieldName).value;
    }
    applyAll() {
        const type = this.form.value.type;
        // determine mode
        if (this.presetTab.isActive) {
            this.store.dispatch(new SetAllChaptersBackground(this.presetBackground));
        }
        else if (this.customTab.isActive) {
            const customBackground = this.formToBackgroundColor(type);
            if (customBackground) {
                this.store.dispatch(new SetAllChaptersBackground(customBackground));
            }
        }
    }
    applyFromCustom() {
        const type = this.form.value.type;
        const customBackground = this.formToBackgroundColor(type);
        if (customBackground) {
            this.store.dispatch(new SetSelectedChapterBackground(customBackground));
        }
    }
    setCustomColorForm(background) {
        let type = 'solid';
        let color1 = '';
        let color2 = '';
        let stop1 = 0;
        let stop2 = 100;
        const { colors, gradientType, colorStops } = background;
        // set colors
        const firstRGB = [
            colors[0],
            colors[1],
            colors[2]
        ];
        const secondRGB = colors.length < 8
            ? firstRGB
            : [
                colors[4],
                colors[5],
                colors[6]
            ];
        color1 = ColorUtils.rgbToHex(firstRGB, false);
        color2 = ColorUtils.rgbToHex(secondRGB, false);
        // set type: handle transparent
        if (BackgroundModel.isTransparent(background)) {
            type = 'transparent';
        }
        else if (isEqual(firstRGB, secondRGB)) {
            type = 'solid';
        }
        else {
            type = gradientType;
        }
        // set stops
        stop1 = colorStops[0];
        stop2 = colorStops[1];
        this.form.patchValue({
            type: type,
            color1: color1,
            stop1: stop1,
            color2: color2,
            stop2: stop2
        });
    }
    formToBackgroundColor(type) {
        const { color1, color2, stop1, stop2 } = this.form.value;
        const formGroup = this.form;
        // special case
        if (type === 'transparent') {
            return transparentBackground;
        }
        // validation
        if (!formGroup.valid) {
            return null;
        }
        let background;
        const colors = [];
        const gradientType = type === 'radial' ? 'radial' : 'linear';
        // set color 1
        if (color1) {
            colors.push(...ColorUtils.hexToRgb(color1, false), 1);
        }
        // set color 2
        if (color2 && type !== 'solid') {
            colors.push(...ColorUtils.hexToRgb(color2, false), 1);
        }
        else { // repeat color
            colors.push(...colors);
        }
        background = new BackgroundModel(colors, gradientType, [stop1, stop2]);
        return background;
    }
}
__decorate([
    Select(StoryChaptersState.getSelectedChapterBackground),
    __metadata("design:type", Observable)
], BackgroundColorDialogComponent.prototype, "background$", void 0);
__decorate([
    Select(StoryChaptersState.getAllChaptersBackground),
    __metadata("design:type", Observable)
], BackgroundColorDialogComponent.prototype, "allChapterBackgrounds$", void 0);
__decorate([
    Select(StoryChaptersState.getSelectedChapterIndex),
    __metadata("design:type", Observable)
], BackgroundColorDialogComponent.prototype, "selectedChapterIndex$", void 0);
